import {
  ref,
  reactive,
  computed,
  watch,
  onUnmounted,
} from 'vue'
import {
  IClientPublishOptions,
  IPublishPacket,
} from '/@/mqtt'
import { useMyStore } from '/@/store'
import {
  DRC_METHOD,
} from '/@/types/drc'
import EventBus from '/@/event-bus'

export interface DeviceTopicInfo{
  sn: string
  pubTopic: string
  subTopic: string
}

type MessageMqtt = (topic: string, payload: Buffer, packet: IPublishPacket) => void | Promise<void>

interface HeartbeatWaiter {
  sessionKey: string;
  resolve: (value: { seq: number; lastResponseAt: number }) => void;
  reject: (reason?: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const DRC_HEARTBEAT_INTERVAL_MS = 1000
const DRC_HEARTBEAT_TIMEOUT_MS = 12000

export function useMqtt (deviceTopicInfo: DeviceTopicInfo) {
  let cacheSubscribeArr: {
    topic: string;
    callback?: MessageMqtt;
  }[] = []
  let heartbeatWaiters: HeartbeatWaiter[] = []

  const store = useMyStore()

  const mqttState = computed(() => {
    return store.state.mqttState
  })

  function publishMqtt (topic: string, body: object, ots?: IClientPublishOptions) {
    // const buffer = Buffer.from(JSON.stringify(body))
    mqttState.value?.publishMqtt(topic, JSON.stringify(body), ots)
  }

  function subscribeMqtt (topic: string, handleMessageMqtt?: MessageMqtt) {
    mqttState.value?.subscribeMqtt(topic)
    const handler = handleMessageMqtt || onMessageMqtt
    mqttState.value?.on('onMessageMqtt', handler)
    cacheSubscribeArr.push({
      topic,
      callback: handler,
    })
  }

  function onMessageMqtt (message: any) {
    if (cacheSubscribeArr.findIndex(item => item.topic === message?.topic) !== -1) {
      const payloadStr = new TextDecoder('utf-8').decode(message?.payload)
      const payloadObj = JSON.parse(payloadStr)
      switch (payloadObj?.method) {
        case DRC_METHOD.HEART_BEAT:
          handleDrcHeartbeat(payloadObj)
          EventBus.emit('droneControlMqttInfo', payloadObj)
          break
        case DRC_METHOD.DELAY_TIME_INFO_PUSH:
        case DRC_METHOD.HSI_INFO_PUSH:
        case DRC_METHOD.OSD_INFO_PUSH:
        case DRC_METHOD.DRC_INITIAL_STATE_SUBSCRIBE:
        case DRC_METHOD.DRC_DRONE_STATE_PUSH:
        case DRC_METHOD.DRC_CAMERA_STATE_PUSH:
        case DRC_METHOD.DRC_CAMERA_OSD_INFO_PUSH:
        case DRC_METHOD.DRC_PSDK_STATE_INFO:
        case DRC_METHOD.DRC_SPEAKER_PLAY_PROGRESS:
        case DRC_METHOD.DRC_AI_INFO_PUSH:
        case DRC_METHOD.DRC_AI_IDENTIFY_SET:
        case DRC_METHOD.DRC_AI_SPOTLIGHT_ZOOM_SET:
        case DRC_METHOD.DRC_AI_SPOTLIGHT_ZOOM_SELECT:
        case DRC_METHOD.DRC_AI_SPOTLIGHT_ZOOM_CONFIRM:
        case DRC_METHOD.DRC_AI_SPOTLIGHT_ZOOM_STOP:
        case DRC_METHOD.OBSTACLE_AVOIDANCE_NOTIFY:
        case DRC_METHOD.DRONE_CONTROL:
        case DRC_METHOD.DRONE_EMERGENCY_STOP:
          EventBus.emit('droneControlMqttInfo', payloadObj)
          break
        default:
          break
      }
    }
  }

  function unsubscribeDrc () {
    // 销毁已订阅事件
    cacheSubscribeArr.forEach(item => {
      mqttState.value?.off('onMessageMqtt', item.callback)
      mqttState.value?.unsubscribeMqtt(item.topic)
    })
    cacheSubscribeArr = []
  }

  // 心跳
  const heartBeatSeq = ref(0)
  const initialStateSeq = ref(0)
  let currentDrcSessionKey = ''
  const state = reactive({
    heartState: new Map<string, {
      pingInterval: any;
    }>(),
    heartbeatActive: false,
    lastHeartbeatAt: 0,
    lastHeartbeatSeq: 0,
  })

  // 监听云控控制权
  watch(() => [deviceTopicInfo.sn, deviceTopicInfo.subTopic, deviceTopicInfo.pubTopic], ([sn, subTopic, pubTopic]) => {
    if (!sn || !subTopic || !pubTopic) {
      stopDrcSession()
      return
    }
    const nextSessionKey = `${sn}|${subTopic}|${pubTopic}`
    if (nextSessionKey === currentDrcSessionKey) {
      return
    }
    stopDrcSession()
    currentDrcSessionKey = nextSessionKey
    // 1.订阅topic
    subscribeMqtt(subTopic)
    // 2.订阅 DRC 初始状态
    publishDrcInitialStateSubscribe()
    // 3.发心跳
    publishDrcPing(sn)
  }, { immediate: true, deep: true })

  function stopDrcSession () {
    state.heartState.forEach(item => clearInterval(item.pingInterval))
    state.heartState.clear()
    heartBeatSeq.value = 0
    initialStateSeq.value = 0
    state.heartbeatActive = false
    state.lastHeartbeatAt = 0
    state.lastHeartbeatSeq = 0
    currentDrcSessionKey = ''
    rejectHeartbeatWaiters(new Error('DRC 链路已重置'))
    unsubscribeDrc()
  }

  function publishDrcInitialStateSubscribe () {
    initialStateSeq.value += 1
    publishMqtt(deviceTopicInfo.pubTopic, {
      method: DRC_METHOD.DRC_INITIAL_STATE_SUBSCRIBE,
      data: {},
      seq: initialStateSeq.value,
    }, { qos: 0 })
  }

  function publishDrcPing (sn: string) {
    const publishHeartbeat = () => {
      if (!mqttState.value || !deviceTopicInfo.pubTopic) return
      heartBeatSeq.value += 1
      publishMqtt(deviceTopicInfo.pubTopic, {
        method: DRC_METHOD.HEART_BEAT,
        data: {
          timestamp: new Date().getTime(),
        },
        seq: heartBeatSeq.value,
      }, { qos: 0 })
    }
    publishHeartbeat()
    const pingInterval = setInterval(publishHeartbeat, DRC_HEARTBEAT_INTERVAL_MS)
    state.heartState.set(sn, {
      pingInterval,
    })
  }

  function handleDrcHeartbeat (payload: any) {
    const seq = Number(payload?.seq)
    state.heartbeatActive = true
    state.lastHeartbeatAt = Date.now()
    state.lastHeartbeatSeq = Number.isFinite(seq) ? seq : state.lastHeartbeatSeq
    resolveHeartbeatWaiters()
  }

  function waitForHeartbeat (timeoutMs = DRC_HEARTBEAT_TIMEOUT_MS) {
    if (state.heartbeatActive && state.lastHeartbeatAt > 0) {
      return Promise.resolve({
        seq: state.lastHeartbeatSeq,
        lastResponseAt: state.lastHeartbeatAt,
      })
    }
    const sessionKey = currentDrcSessionKey
    if (!sessionKey) {
      return Promise.reject(new Error('DRC topic 未建立'))
    }
    return new Promise<{ seq: number; lastResponseAt: number }>((resolve, reject) => {
      const timer = setTimeout(() => {
        heartbeatWaiters = heartbeatWaiters.filter(waiter => waiter.timer !== timer)
        reject(new Error('DRC 心跳无响应，请检查 DRC MQTT 链路后重试'))
      }, timeoutMs)
      heartbeatWaiters.push({
        sessionKey,
        resolve,
        reject,
        timer,
      })
    })
  }

  function resolveHeartbeatWaiters () {
    const activeWaiters = heartbeatWaiters.filter(waiter => waiter.sessionKey === currentDrcSessionKey)
    heartbeatWaiters = heartbeatWaiters.filter(waiter => waiter.sessionKey !== currentDrcSessionKey)
    activeWaiters.forEach(waiter => {
      clearTimeout(waiter.timer)
      waiter.resolve({
        seq: state.lastHeartbeatSeq,
        lastResponseAt: state.lastHeartbeatAt,
      })
    })
  }

  function rejectHeartbeatWaiters (error: Error) {
    heartbeatWaiters.forEach(waiter => {
      clearTimeout(waiter.timer)
      waiter.reject(error)
    })
    heartbeatWaiters = []
  }

  onUnmounted(() => {
    stopDrcSession()
  })

  return {
    mqttState,
    drcState: state,
    publishMqtt,
    subscribeMqtt,
    waitForHeartbeat,
  }
}
